import {
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import type { MetaAdsLeadFormDraft } from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export const useLeads = (formId?: string) => {
	const company = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.Leads(company.id, formId),
		queryFn: () => Api.GetMetaAdsLeads(formId).then((r) => r.data),
	});
};

export const useLeadForms = () => {
	const company = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.LeadForms(company.id),
		queryFn: () => Api.GetMetaAdsLeadForms().then((r) => r.data),
	});
};

export const useMetaLeadForms = () => {
	const company = useCompany();
	return useQuery({
		queryKey: QueryKeys.Integrations.MetaAds.MetaLeadForms(company.id),
		queryFn: () => Api.GetMetaAdsMetaLeadForms().then((r) => r.data),
	});
};

export const useLeadActions = () => {
	const company = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const saveForm = useMutation({
		mutationFn: (form: MetaAdsLeadFormDraft) =>
			Api.SaveMetaAdsLeadForm(form).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.LeadForms(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Lead form saved." });
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to save lead form." });
		},
	});

	const deleteForm = useMutation({
		mutationFn: (formId: string) =>
			Api.DeleteMetaAdsLeadForm(formId).then((r) => r.data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.LeadForms(company.id),
			});
			emit({ type: AlertType.SUCCESS, content: "Lead form deleted." });
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to delete lead form.",
			});
		},
	});

	const syncLeads = useMutation({
		mutationFn: () => Api.SyncMetaAdsLeads().then((r) => r.data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Integrations.MetaAds.Leads(company.id),
			});
			emit({
				type: AlertType.SUCCESS,
				content: `Synced ${data.syncedCount} leads.`,
			});
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to sync leads." });
		},
	});

	const loadForm = async (
		formId: string,
	): Promise<MetaAdsLeadFormDraft | null> => {
		try {
			return (await Api.GetMetaAdsLeadForm(formId)).data;
		} catch {
			return null;
		}
	};

	return { saveForm, deleteForm, syncLeads, loadForm };
};
